/**
 * @fileoverview V2 Documents Step — Retainer/HIPAA tracking + file uploads.
 *
 * Reuses V1 subcomponents for document received cards and file upload.
 * Audit-tracked: updates retainer_document, hipaa_document sections.
 */
import { AlertCircle, Check, Eye, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { formatFileSize } from '@/components/documents-v2/utils/fileUtils';
import { FullDocumentViewer } from '@/components/initial-review/components/FullDocumentViewer';
import { DocumentReceivedCard } from '@/components/case-ingestion/steps/documents/DocumentReceivedCard';
import { useDestroyBlobMutation, useGetBlobsQuery } from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import {
  useGetCaseIngestionQuery,
  useUpdateCaseIngestionSectionsMutation,
} from '@/services/api/caseIngestionService';
import type { IngestionSection, IngestionSectionStatus } from '@/services/types/case-ingestion-types';
import { useFilevineIngestionStatusQuery } from '@/services/api/generated/filevineSyncService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import { getCmsTargetSystemFromUrl, hasCmsAutoSync } from '@/utils/externalSystemUtils';

import { FormGroup, FormSection } from '../components/form';
import { useIngestion } from '../state/IngestionProvider';

export const DocumentsStep: React.FC = () => {
  const { state, dispatch, registerSave } = useIngestion();
  const matterId = state.matterId!;

  const [previewArtifactId, setPreviewArtifactId] = useState<string | null>(null);
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────
  const { data: matter } = useGetMatterDetailsQuery(matterId);
  const { data: auditRecord } = useGetCaseIngestionQuery(matterId);
  const [updateSections, { isLoading: isUpdatingSections }] = useUpdateCaseIngestionSectionsMutation();

  const retainerSectionStatus = auditRecord?.sections?.retainer_document ?? null;
  const hipaaSectionStatus = auditRecord?.sections?.hipaa_document ?? null;

  const retainerStatus = retainerSectionStatus === 'found' ? 'received' as const
    : retainerSectionStatus === 'not_found' ? 'not_received' as const
    : null;
  const hipaaStatus = hipaaSectionStatus === 'found' ? 'received' as const
    : hipaaSectionStatus === 'not_found' ? 'not_received' as const
    : null;

  const handleReceived = useCallback(async (type: 'retainer' | 'hipaa') => {
    const section: IngestionSection = type === 'retainer' ? 'retainer_document' : 'hipaa_document';
    try {
      await updateSections({ matterId, data: { sections: { [section]: 'found' } } }).unwrap();
      toast.success(`${type === 'retainer' ? 'Retainer' : 'HIPAA'} marked as received`);
    } catch { toast.error('Failed to update'); }
  }, [matterId, updateSections]);

  const handleNotReceived = useCallback(async (type: 'retainer' | 'hipaa') => {
    const section: IngestionSection = type === 'retainer' ? 'retainer_document' : 'hipaa_document';
    try {
      await updateSections({ matterId, data: { sections: { [section]: 'not_found' } } }).unwrap();
      toast.success(`${type === 'retainer' ? 'Retainer' : 'HIPAA'} marked as not received`);
    } catch { toast.error('Failed to update'); }
  }, [matterId, updateSections]);

  const isTaskLoading = isUpdatingSections;

  const detectedCms = useMemo(() => getCmsTargetSystemFromUrl(matter?.external_cms_url), [matter?.external_cms_url]);
  const hasAutoSync = useMemo(() => hasCmsAutoSync(detectedCms), [detectedCms]);

  const { data: existingBlobs, isLoading: isLoadingBlobs, refetch: refetchBlobs } = useGetBlobsQuery(matterId);
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId);
  const rootCollectionId = collectionTree?.id;

  const { uploadProgress, handleUploadToCollection } = useUploadManagerUnified({ matterId, refetch: refetchBlobs });
  const [destroyBlob, { isLoading: isDeleting }] = useDestroyBlobMutation();

  const { data: ingestionStatus } = useFilevineIngestionStatusQuery({ matterId }, { pollingInterval: 5000 });
  const isFilevineSyncRunning = ingestionStatus?.status === 'IN_PROGRESS';
  const isFilevineSyncFailed = ingestionStatus?.status === 'FAILED';

  useEffect(() => {
    if (ingestionStatus?.status === 'COMPLETED') refetchBlobs();
  }, [ingestionStatus?.status, refetchBlobs]);

  const isUploading = uploadProgress.some((p) => p.status === 'uploading' || p.status === 'pending');

  // ── Upload handlers ────────────────────────────────────────────────────
  const uploadFiles = useCallback(async (files: File[]) => {
    if (!rootCollectionId || files.length === 0) return;
    await handleUploadToCollection(files, rootCollectionId, false, false);
  }, [rootCollectionId, handleUploadToCollection]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    await uploadFiles(Array.from(e.dataTransfer.files));
  }, [uploadFiles]);
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) uploadFiles(Array.from(e.target.files));
  }, [uploadFiles]);

  const handleDeleteFile = async (blobId: string) => {
    try { await destroyBlob({ matterId, blobId }).unwrap(); refetchBlobs(); }
    catch { toast.error('Failed to delete file'); }
  };

  // ── Save ───────────────────────────────────────────────────────────────
  const saveForm = useCallback(async (): Promise<boolean> => {
    toast.success('Documents step completed');
    return true;
  }, []);

  useEffect(() => {
    registerSave(saveForm);
    return () => registerSave(null);
  }, [registerSave, saveForm]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await saveForm();
    if (success) dispatch({ type: 'GO_TO_STEP', index: state.stepIndex + 1 });
  };

  // ── Preview mode ───────────────────────────────────────────────────────
  if (previewArtifactId) {
    return (
      <div className="flex flex-col h-[calc(100vh-180px)]">
        <FullDocumentViewer artifactId={previewArtifactId} matterId={matterId} onBack={() => setPreviewArtifactId(null)} />
      </div>
    );
  }

  const inProgressUploads = uploadProgress.filter(
    (p) => (p.status === 'uploading' || p.status === 'pending') && !existingBlobs?.some((b) => b.original_filename === p.fileName),
  );
  const totalFileCount = (existingBlobs?.length ?? 0) + inProgressUploads.length;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {/* Intake Documents */}
      <FormSection title="Intake Documents" description="Mark intake documents as received">
        <FormGroup>
          <DocumentReceivedCard
            title="Signed Retainer Agreement" badge="R"
            status={retainerStatus}
            onReceivedClick={() => handleReceived('retainer')}
            onNotReceivedClick={() => handleNotReceived('retainer')}
            isLoading={isTaskLoading || isFilevineSyncRunning}
          />
          <DocumentReceivedCard
            title="Signed HIPAA Directive" badge="H"
            status={hipaaStatus}
            onReceivedClick={() => handleReceived('hipaa')}
            onNotReceivedClick={() => handleNotReceived('hipaa')}
            isLoading={isTaskLoading || isFilevineSyncRunning}
          />
        </FormGroup>
      </FormSection>

      {/* Sync banners */}
      {isFilevineSyncRunning && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <p className="text-xs font-medium text-blue-700">Importing documents from Filevine...</p>
          </div>
        </div>
      )}
      {isFilevineSyncFailed && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <p className="text-xs font-medium text-red-700">Unable to import documents. Upload manually below.</p>
          </div>
        </div>
      )}

      {/* File Upload */}
      <FormSection title="Files" description="Upload additional relevant documents">
        <FormGroup>
          {hasAutoSync && !showManualUpload && !isFilevineSyncRunning && !isFilevineSyncFailed ? (
            <div className="flex items-start gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2">
              <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-zinc-900">Automated Document Syncing</p>
                <p className="text-xs text-zinc-500 mt-0.5">Documents will be pulled automatically from Filevine.</p>
                <button type="button" className="text-xs text-zinc-500 underline mt-1 hover:text-zinc-700" onClick={() => setShowManualUpload(true)}>
                  Upload manually instead
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-zinc-200 hover:border-zinc-300'}`}
              onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
            >
              <Upload className="h-8 w-8 mx-auto text-zinc-400 mb-2" />
              <p className="text-sm font-medium text-zinc-900 mb-1">Drag and drop files here</p>
              <p className="text-xs text-zinc-500 mb-3">or</p>
              <button
                type="button"
                disabled={isUploading || isFilevineSyncRunning}
                onClick={() => document.getElementById('v2-file-upload')?.click()}
                className="inline-flex items-center rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              >
                Browse Files
              </button>
              <input id="v2-file-upload" type="file" multiple onChange={handleFileSelect} className="hidden" />
              <p className="text-xs text-zinc-400 mt-3">PDF, Word, Excel, Images supported</p>
            </div>
          )}

          {/* File list */}
          {totalFileCount > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-zinc-700">Files ({totalFileCount})</p>
              {inProgressUploads.map((item) => (
                <div key={item.fileName} className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
                  <Loader2 className="h-3.5 w-3.5 text-blue-500 shrink-0 animate-spin" />
                  <p className="text-xs font-medium text-zinc-900 truncate flex-1">{item.fileName}</p>
                  <span className="text-xs text-zinc-500">{Math.round(item.progress)}%</span>
                </div>
              ))}
              {existingBlobs?.map((blob) => (
                <div key={blob.id} className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2">
                  <FileText className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                  <p className="text-xs font-medium text-zinc-900 truncate flex-1">{blob.original_filename || blob.name}</p>
                  <span className="text-xs text-zinc-500">{formatFileSize(blob.file_size)}</span>
                  {blob.artifact_id && (
                    <button type="button" onClick={() => setPreviewArtifactId(blob.artifact_id!)} className="p-1 text-zinc-400 hover:text-blue-600"><Eye className="h-3.5 w-3.5" /></button>
                  )}
                  <button type="button" onClick={() => handleDeleteFile(blob.id)} disabled={isDeleting} className="p-1 text-zinc-400 hover:text-red-500 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </FormGroup>
      </FormSection>

      <button type="submit" className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors duration-150">
        Save & Continue
      </button>
    </form>
  );
};
